import React, { useState } from 'react';
import './ThirdStep.scss';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../../../../shared/contexts/account/AccountContexts';
import { useProjectWizard } from '../../contexts/ProjectWizardContext';
import ProjectSetupLoader from '../project-setup-loader/ProjectSetupLoader';
import { useProjectSubmit } from '../../hooks/UseProjectSubmit';
import { useModuleAccess } from '../../../../../shared/hooks/global/UseModuleAccess';
import { MONITORING_SCHEDULE_PRESETS } from '../../../../../shared/models/projects/MonitoringInterval';

const ThirdStep: React.FC = () => {
  const { submitProject, createdProjectId, loaderCompleted, setupProgress, leaveSetup } =
    useProjectSubmit();
  const [apiError, setApiError] = useState<string | null>(null);
  const {
    activeStep,
    setActiveStep,
    projectData,
    searchVolume,
    questionResponse,
    setQuestionResponse,
    monitoringPreset,
  } = useProjectWizard();
  const { accountResponse, refetchData } = useAccount();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // The schedule was chosen in the previous step (under the country selector) and rides along here; the
  // account's own addons decide, a full super admin passes without the addon.
  const scheduleAccess = useModuleAccess('MANAGE_MONITORING_SCHEDULE', null, { allowSuperAdmin: true });

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    if (activeStep !== 'SEARCH_VOLUME') {
      return;
    }

    if (!projectData || projectData.questions.length === 0) {
      setApiError(t('selectMinimumOne'));
      return;
    }

    setApiError(null);
    setIsSubmitting(true);

    try {
      await submitProject(
        scheduleAccess.canAccess
          ? { ...projectData, monitoringInterval: MONITORING_SCHEDULE_PRESETS[monitoringPreset] }
          : projectData
      );
    } catch (err: any) {
      setApiError(err.response.data.error || t('unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const backToQuestion = () => {
    if (
      !projectData ||
      projectData.questions.length <= 0 ||
      !questionResponse ||
      questionResponse.brand.trim() === ''
    ) {
      setActiveStep('QUESTIONS');
      return;
    }

    setQuestionResponse({
      ids: questionResponse.ids,
      questions: projectData.questions,
      brand: projectData.firmName,
      projectCountry: projectData.projectCountry,
    });
    setActiveStep('QUESTIONS');
  };

  return (
    <div className="project-wizard__page-box project-wizard__page-box--question">
      {isSubmitting && (
        <ProjectSetupLoader
          progress={setupProgress}
          projectId={createdProjectId}
          completed={loaderCompleted}
          onNavigate={leaveSetup}
        />
      )}

      <div className="project-wizard__form">
        <div className="form-box__row">
          <h2>{t('searchVolumeForSelectedQuestions')}:</h2>
          <div className="form-table">
            <div className="form-table__row form-table__row--header">
              <div>{t('question')}:</div>
              <div>{t('searchVolume')}:</div>
            </div>
            {searchVolume?.map((searchVolumeData, index) => (
              <div className="form-table__row" key={`question-${index}`}>
                <div>
                  {searchVolumeData.question}
                  {accountResponse?.isSuperAdmin && !accountResponse?.isLimitedSuperAdmin && (
                    <span>{searchVolumeData.phrase}</span>
                  )}
                </div>
                <div>
                  {searchVolumeData.searchVolume > 100
                    ? Intl.NumberFormat('pl-PL').format(searchVolumeData.searchVolume)
                    : '<100'}
                </div>
              </div>
            ))}
          </div>
          {apiError && <span className="error-message">{apiError}</span>}
        </div>
      </div>

      <div className="project-wizard__action project-wizard__action--inline">
        <Button
          onClick={backToQuestion}
          type="submit"
          className="btn btn--secondary btn--xxl"
          disabled={isSubmitting}
        >
          {t('back')}
        </Button>
        <Button onClick={handleSubmit} type="submit" className="btn btn--xxl" disabled={isSubmitting}>
          {t('saveAndLaunch')}
        </Button>
      </div>
    </div>
  );
};

export default ThirdStep;
